import { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutChangeEvent, LayoutRectangle, StyleSheet, View } from 'react-native';

import {
  ActionSheet,
  IconCheckFill,
  IconRewardsFill,
  IconStarRateFill,
  Text,
  tokens,
} from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import QrCode from '@fhs-legacy/frontend/src/pages/in-store-rewards/qr-code';
import {
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from '@fhs-legacy/frontend/src/state/loyalty/in-restaurant-redemption';

import { useLoyaltyIncentives } from '../../queries/loyalty.queries';

import { DisplayCard } from './display-card';
import { CardContent } from './reward-tier-card-content';

export function InRestaurantRedemption() {
  const { data } = useLoyaltyIncentives();
  const [layout, setLayout] = useState<LayoutRectangle>();

  const { inStoreRewardTierId, resetInRestaurantRedemption, shortCodeState } =
    useInRestaurantRedemptionContext();

  const resetInRestaurantRedemptionRef = useBoxRef(resetInRestaurantRedemption);

  const isClaimed = shortCodeState === ShortCodeState.Claimed;

  const selectedRewardTier = useMemo(() => {
    return data?.rewardTiers?.find(tier => tier.id === inStoreRewardTierId);
  }, [data?.rewardTiers, inStoreRewardTierId]);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setLayout(event.nativeEvent.layout);
  }, []);

  const options = useMemo(() => (layout ? { width: layout.width - 64 } : undefined), [layout]);

  const onPressChange = useCallback(() => {
    resetInRestaurantRedemption();
  }, [resetInRestaurantRedemption]);

  useEffect(() => {
    return () => {
      if (isClaimed) {
        resetInRestaurantRedemptionRef.current?.();
      }
    };
  }, [isClaimed, resetInRestaurantRedemptionRef]);

  if (isClaimed) {
    return (
      <View style={styles.claimedContainer}>
        <View style={styles.iconContainer}>
          <IconRewardsFill size={48} color={tokens.colors.$success} />
          <IconCheckFill
            size={20}
            color={tokens.colors.$success}
            style={{ position: 'absolute', top: 3, right: 10 }}
          />
        </View>
        <Text.Heading type="one" style={{ textAlign: 'center' }}>
          Reward Claimed!
        </Text.Heading>
        <Text.Ui size="md" style={{ textAlign: 'center' }}>
          Congratulations! The cashier has claimed your reward.
        </Text.Ui>
        {selectedRewardTier && (
          <DisplayCard style={{ alignSelf: 'stretch' }} image={selectedRewardTier.image}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{ flex: 1 }}>
                <Text.Ui size="md" weight="semibold" style={{ color: tokens.colors.$houseDark }}>
                  {selectedRewardTier.name}
                </Text.Ui>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <IconStarRateFill size={24} color={tokens.colors.$houseYellow} />
                <Text.Ui size="lg" weight="semibold" style={{ color: tokens.colors.$houseDark }}>
                  {selectedRewardTier.points?.toLocaleString()}
                </Text.Ui>
              </View>
            </View>
          </DisplayCard>
        )}
      </View>
    );
  }

  return (
    <ActionSheet.ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {selectedRewardTier && (
        <DisplayCard
          style={{ alignSelf: 'stretch' }}
          image={selectedRewardTier.image}
          button={{
            onPress: onPressChange,
            label: 'Change',
          }}
        >
          <CardContent
            title={selectedRewardTier.name}
            description={selectedRewardTier.message}
            isRedeemable
          />
        </DisplayCard>
      )}

      <Text.Heading type="one" style={{ marginTop: 16 }}>
        Your Reward is Available!
      </Text.Heading>
      <Text.Ui size="md" style={styles.description}>
        Present your QR Code to a cashier and enjoy your free reward with a purchase:
      </Text.Ui>
      <View style={styles.qrCodeContainer} onLayout={onLayout}>
        <QrCode
          options={options}
          borderColor={tokens.colors.$success}
          numberContainerBackgroundColor="#387F491A"
          phoneDisclaimer="Or use your phone number instead:"
        />
      </View>
    </ActionSheet.ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  claimedContainer: {
    flex: 1,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingBottom: 24,
  },
  iconContainer: {
    padding: 12,
    backgroundColor: tokens.colors.$blackOpacity04,
    borderRadius: 100,
  },
  scrollContent: {
    alignItems: 'center',
  },
  description: {
    marginTop: 8,
    textAlign: 'center',
  },
  qrCodeContainer: {
    width: '100%',
    marginTop: 16,
    flex: 1,
  },
});
